/**
 * Construction Issues & Approvals — Common Domain Models and Validators
 * R39 B5.a + B5.b (plan docs/plans/sector-operations.md §2 B5)
 */

import { type OutsideApprovalMode } from './approvals.js'

export const CON_ISSUE_KINDS = [
  'HINDRANCE',
  'RFI',
  'SELECTION',
  'CHANGE_REQUEST',
] as const
export type ConIssueKind = (typeof CON_ISSUE_KINDS)[number]

export const CON_ISSUE_STATES = [
  'OPEN',
  'RESPONDED',
  'DECIDED',
  'CLOSED',
  'WITHDRAWN',
] as const
export type ConIssueState = (typeof CON_ISSUE_STATES)[number]

export const CON_RESPONSIBLE_PARTY_TYPES = [
  'INTERNAL',
  'SUBCONTRACTOR',
  'CLIENT',
  'CONSULTANT',
] as const
export type ConResponsiblePartyType = (typeof CON_RESPONSIBLE_PARTY_TYPES)[number]

export const CON_IMPACT_LEVELS = [
  'NONE',
  'POSSIBLE',
  'LIKELY',
  'CONFIRMED',
] as const
export type ConImpactLevel = (typeof CON_IMPACT_LEVELS)[number]

export const CON_DECISION_VIAS = [
  'INTERNAL',
  'OUTSIDE_APPROVAL',
] as const
export type ConDecisionVia = (typeof CON_DECISION_VIAS)[number]

export const MIN_CON_ISSUE_TITLE_LENGTH = 3
export const MAX_CON_ISSUE_TITLE_LENGTH = 120
export const MAX_CON_ISSUE_DESCRIPTION_LENGTH = 4000
export const MAX_CON_ISSUE_NOTE_LENGTH = 2000
export const MAX_CON_ISSUE_REVISED_INSTRUCTION_LENGTH = 4000
export const MAX_CON_ISSUE_CONTRACT_NOTICE_LENGTH = 120
export const MIN_CON_ISSUE_REASON_LENGTH = 3
export const MAX_CON_ISSUE_REASON_LENGTH = 500
export const MAX_CON_ISSUE_PHOTOS = 10
export const CON_ISSUE_PHOTO_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const
export type ConIssuePhotoContentType = (typeof CON_ISSUE_PHOTO_CONTENT_TYPES)[number]
export const MAX_CON_ISSUE_PHOTO_BYTES = 10 * 1024 * 1024
export const MIN_SELECTION_OPTIONS = 2
export const MAX_SELECTION_OPTIONS = 10
export const MAX_OPTION_LABEL_LENGTH = 120
export const MAX_OPTION_NOTE_LENGTH = 500

export interface ConResponsibleParty {
  type: ConResponsiblePartyType
  uid?: string
  name: string
}

export interface ConIssueImpact {
  note?: string
  time: ConImpactLevel
  cost: ConImpactLevel
}

export interface ConSelectionOption {
  id: string
  label: string
  note?: string
}

export interface ConDrawingReference {
  drawingId: string
  revisionId?: string
}

export interface ConIssue {
  id: string
  workspaceId: string
  projectId: string
  siteId: string
  kind: ConIssueKind
  registerNo: string
  title: string
  description: string
  firstNoticedAt: number
  firstNoticedDay: string
  placeId: string
  responsibleParty: ConResponsibleParty
  drawingRef?: ConDrawingReference
  contractNoticeRef?: string
  responseDueAt: number
  impact: ConIssueImpact
  photoPaths: string[]
  options?: ConSelectionOption[]
  state: ConIssueState
  requestId: string
  currentDecisionVersion: number
  responseCount?: number
  approvalId?: string
  approvalState?: string
  requestCloseError?: {
    code: string
    at: number
  }
  decidedAt?: number
  decidedBy?: string
  withdrawnAt?: number
  withdrawnBy?: string
  withdrawnReason?: string
  closedAt?: number
  closedBy?: string
  createdBy: string
  createdAt: number
  updatedAt: number
}

export interface ConIssueEdit {
  id: string
  issueId: string
  byUid: string
  at: number
  diff: Record<string, { old: unknown; new: unknown }>
}

export interface ConIssueResponse {
  id: string
  issueId: string
  index: number
  text: string
  photoPaths: string[]
  byUid: string
  at: number
}

export interface ConIssueDecision {
  version: number
  text: string
  chosenOptionId?: string
  revisedInstruction?: string
  decidedBy: string
  decidedVia: ConDecisionVia
  approvalId?: string
  supersedesVersion?: number
  supersedeReason?: string
  selfDecisionReason?: string
  at: number
}

export interface ConIssueDetailResult {
  issue: ConIssue
  responses: ConIssueResponse[]
  decisions: ConIssueDecision[]
  edits: ConIssueEdit[]
}

export interface CreateConIssueInput {
  workspaceId: string
  projectId: string
  placeId: string
  kind: ConIssueKind
  title: string
  description?: string
  firstNoticedAt: number
  responsibleParty: ConResponsibleParty
  drawingRef?: ConDrawingReference
  contractNoticeRef?: string
  responseDueAt: number
  impact: ConIssueImpact
  photoPaths?: string[]
  options?: ConSelectionOption[]
  opId?: string
}

export interface UpdateConIssueInput {
  workspaceId: string
  issueId: string
  projectId?: string
  title?: string
  description?: string
  placeId?: string
  responsibleParty?: ConResponsibleParty
  drawingRef?: ConDrawingReference | null
  contractNoticeRef?: string | null
  responseDueAt?: number
  impact?: ConIssueImpact
  photoPaths?: string[]
  options?: ConSelectionOption[]
  opId?: string
}

export interface RespondConIssueInput {
  workspaceId: string
  issueId: string
  projectId?: string
  text: string
  photoPaths?: string[]
  opId?: string
}

export interface WithdrawConIssueInput {
  workspaceId: string
  issueId: string
  projectId?: string
  reason: string
  opId?: string
}

export interface CloseConIssueInput {
  workspaceId: string
  issueId: string
  projectId?: string
  opId?: string
}

export interface DecideConIssueInput {
  workspaceId: string
  issueId: string
  projectId?: string
  text: string
  chosenOptionId?: string
  revisedInstruction?: string
  supersedeReason?: string
  selfDecisionReason?: string
  opId?: string
}

export interface SendConIssueForApprovalInput {
  workspaceId: string
  issueId: string
  projectId?: string
  approvers: Array<{ name: string; phone: string }>
  mode?: OutsideApprovalMode
  expiresInDays?: number
  message?: string
  opId?: string
}

export interface GetConIssueInput {
  workspaceId: string
  issueId: string
  projectId?: string
}

export interface AttachConIssuePhotosInput {
  workspaceId: string
  issueId: string
  projectId?: string
  photoPaths: string[]
  opId?: string
}

export interface GetConIssuePhotoUrlsInput {
  workspaceId: string
  issueId: string
  projectId?: string
}

export interface ConIssueSignedPhoto {
  path: string
  url: string
}

export interface GetConIssuePhotoUrlsResult {
  issue: ConIssueSignedPhoto[]
  responses: Record<number, ConIssueSignedPhoto[]>
  skipped?: number
}

export interface ListConIssuesInput {
  workspaceId: string
  projectId: string
  kind?: ConIssueKind
  state?: ConIssueState
  overdueOnly?: boolean
  limit?: number
}

export interface ListConIssueDecisionsInput {
  workspaceId: string
  projectId?: string
  issueId: string
  limit?: number
}

// ---------------------------------------------------------------------------
// Pure Validators
// ---------------------------------------------------------------------------

export function validateConIssueKind(val: unknown): ConIssueKind {
  if (typeof val !== 'string' || !CON_ISSUE_KINDS.includes(val as ConIssueKind)) {
    throw new Error(
      `Invalid issue kind. Must be one of: ${CON_ISSUE_KINDS.join(', ')}`,
    )
  }
  return val as ConIssueKind
}

export function validateConIssueTitle(val: unknown): string {
  if (typeof val !== 'string') {
    throw new Error('Issue title must be a string.')
  }
  const trimmed = val.trim()
  if (
    trimmed.length < MIN_CON_ISSUE_TITLE_LENGTH ||
    trimmed.length > MAX_CON_ISSUE_TITLE_LENGTH
  ) {
    throw new Error(
      `Issue title must be between ${MIN_CON_ISSUE_TITLE_LENGTH} and ${MAX_CON_ISSUE_TITLE_LENGTH} characters.`,
    )
  }
  return trimmed
}

export function validateConIssueDescription(val: unknown): string {
  if (val === undefined || val === null || val === '') {
    return ''
  }
  if (typeof val !== 'string') {
    throw new Error('Issue description must be a string.')
  }
  const trimmed = val.trim()
  if (trimmed.length > MAX_CON_ISSUE_DESCRIPTION_LENGTH) {
    throw new Error(
      `Issue description must not exceed ${MAX_CON_ISSUE_DESCRIPTION_LENGTH} characters.`,
    )
  }
  return trimmed
}

export function validateConResponsibleParty(val: unknown): ConResponsibleParty {
  if (!val || typeof val !== 'object') {
    throw new Error('Responsible party must be an object.')
  }
  const rec = val as Record<string, unknown>
  const type = rec.type
  if (
    typeof type !== 'string' ||
    !CON_RESPONSIBLE_PARTY_TYPES.includes(type as ConResponsiblePartyType)
  ) {
    throw new Error(
      `Responsible party type must be one of: ${CON_RESPONSIBLE_PARTY_TYPES.join(', ')}`,
    )
  }
  const name = typeof rec.name === 'string' ? rec.name.trim() : ''
  if (name.length < 1 || name.length > 120) {
    throw new Error('Responsible party name must be between 1 and 120 characters.')
  }
  let uid: string | undefined
  if (type === 'INTERNAL') {
    if (typeof rec.uid === 'string' && rec.uid.trim()) {
      uid = rec.uid.trim()
    }
  }
  return {
    type: type as ConResponsiblePartyType,
    name,
    ...(uid ? { uid } : {}),
  }
}

export function validateConImpact(val: unknown): ConIssueImpact {
  if (!val || typeof val !== 'object') {
    throw new Error('Impact must be an object.')
  }
  const rec = val as Record<string, unknown>
  // Reject any numeric money fields
  for (const k of Object.keys(rec)) {
    if (
      k.toLowerCase().includes('cost') &&
      typeof rec[k] === 'number'
    ) {
      throw new Error(`Numeric money field "${k}" is not allowed in construction impact.`)
    }
    if (
      (k.toLowerCase().includes('amount') ||
        k.toLowerCase().includes('money') ||
        k.toLowerCase().includes('price')) &&
      typeof rec[k] === 'number'
    ) {
      throw new Error(`Numeric money field "${k}" is not allowed in construction impact.`)
    }
  }

  const time = rec.time
  if (
    typeof time !== 'string' ||
    !CON_IMPACT_LEVELS.includes(time as ConImpactLevel)
  ) {
    throw new Error(
      `Impact time must be one of: ${CON_IMPACT_LEVELS.join(', ')}`,
    )
  }
  const cost = rec.cost
  if (
    typeof cost !== 'string' ||
    !CON_IMPACT_LEVELS.includes(cost as ConImpactLevel)
  ) {
    throw new Error(
      `Impact cost must be one of: ${CON_IMPACT_LEVELS.join(', ')}`,
    )
  }
  let note: string | undefined
  if (typeof rec.note === 'string') {
    const trimmed = rec.note.trim()
    if (trimmed.length > MAX_CON_ISSUE_NOTE_LENGTH) {
      throw new Error(
        `Impact note must not exceed ${MAX_CON_ISSUE_NOTE_LENGTH} characters.`,
      )
    }
    note = trimmed
  }
  return {
    time: time as ConImpactLevel,
    cost: cost as ConImpactLevel,
    ...(note !== undefined ? { note } : {}),
  }
}

export function validateConSelectionOptions(
  options: unknown,
  kind: ConIssueKind,
): ConSelectionOption[] | undefined {
  if (kind === 'SELECTION') {
    if (!Array.isArray(options)) {
      throw new Error('Selection options must be an array for SELECTION kind.')
    }
    if (
      options.length < MIN_SELECTION_OPTIONS ||
      options.length > MAX_SELECTION_OPTIONS
    ) {
      throw new Error(
        `Selection options must contain between ${MIN_SELECTION_OPTIONS} and ${MAX_SELECTION_OPTIONS} items.`,
      )
    }
    const seenIds = new Set<string>()
    const cleaned: ConSelectionOption[] = []
    for (const opt of options) {
      if (!opt || typeof opt !== 'object') {
        throw new Error('Option must be an object.')
      }
      const rec = opt as Record<string, unknown>
      const id = typeof rec.id === 'string' ? rec.id.trim() : ''
      if (!id || id.length > 60) {
        throw new Error('Option id must be a non-empty string up to 60 characters.')
      }
      if (seenIds.has(id)) {
        throw new Error(`Duplicate option id "${id}".`)
      }
      seenIds.add(id)
      const label = typeof rec.label === 'string' ? rec.label.trim() : ''
      if (label.length < 1 || label.length > MAX_OPTION_LABEL_LENGTH) {
        throw new Error(
          `Option label must be between 1 and ${MAX_OPTION_LABEL_LENGTH} characters.`,
        )
      }
      let note: string | undefined
      if (typeof rec.note === 'string' && rec.note.trim()) {
        const trimmedNote = rec.note.trim()
        if (trimmedNote.length > MAX_OPTION_NOTE_LENGTH) {
          throw new Error(
            `Option note must not exceed ${MAX_OPTION_NOTE_LENGTH} characters.`,
          )
        }
        note = trimmedNote
      }
      cleaned.push({
        id,
        label,
        ...(note ? { note } : {}),
      })
    }
    return cleaned
  }

  // Non-selection kinds
  if (Array.isArray(options) && options.length > 0) {
    throw new Error('Options are only allowed for SELECTION kind issues.')
  }
  return undefined
}

export function validateConPhotoPaths(val: unknown): string[] {
  if (val === undefined || val === null) {
    return []
  }
  if (!Array.isArray(val)) {
    throw new Error('photoPaths must be an array.')
  }
  if (val.length > MAX_CON_ISSUE_PHOTOS) {
    throw new Error(
      `photoPaths cannot contain more than ${MAX_CON_ISSUE_PHOTOS} photos.`,
    )
  }
  const result: string[] = []
  for (const item of val) {
    if (typeof item !== 'string' || !item.trim()) {
      throw new Error('Each photoPath must be a non-empty string.')
    }
    const trimmed = item.trim()
    if (trimmed.length > 500) {
      throw new Error('Each photoPath must be 500 characters or fewer.')
    }
    result.push(trimmed)
  }
  return result
}

export function validateConIssuePhotoPath(
  path: unknown,
  workspaceId: string,
  issueId: string,
  uid?: string,
): string {
  if (typeof path !== 'string' || !path.trim()) {
    throw new Error('Photo path must be a non-empty string.')
  }
  const trimmed = path.trim()
  if (trimmed.includes('..')) {
    throw new Error('Photo path cannot contain "..".')
  }
  if (uid) {
    const prefix = `workspaces/${workspaceId}/con_issues/${issueId}/${uid}/`
    if (!trimmed.startsWith(prefix)) {
      throw new Error(`Photo path "${trimmed}" must start with "${prefix}".`)
    }
    const fileName = trimmed.slice(prefix.length)
    if (!fileName || fileName.includes('/')) {
      throw new Error(`Photo path "${trimmed}" must have exactly one file segment after prefix.`)
    }
    return trimmed
  }
  const prefix = `workspaces/${workspaceId}/con_issues/${issueId}/`
  if (!trimmed.startsWith(prefix)) {
    throw new Error(`Photo path "${trimmed}" must start with "${prefix}".`)
  }
  const rest = trimmed.slice(prefix.length)
  const segments = rest.split('/')
  if (segments.length !== 2 || !segments[0] || !segments[1]) {
    throw new Error(`Photo path "${trimmed}" must match "workspaces/${workspaceId}/con_issues/${issueId}/{uid}/{fileName}".`)
  }
  return trimmed
}

export function validateConContractNoticeRef(val: unknown): string | undefined {
  if (val === undefined || val === null || val === '') {
    return undefined
  }
  if (typeof val !== 'string') {
    throw new Error('contractNoticeRef must be a string.')
  }
  const trimmed = val.trim()
  if (trimmed.length > MAX_CON_ISSUE_CONTRACT_NOTICE_LENGTH) {
    throw new Error(
      `contractNoticeRef must not exceed ${MAX_CON_ISSUE_CONTRACT_NOTICE_LENGTH} characters.`,
    )
  }
  return trimmed
}

export function validateFirstNoticedAt(val: unknown, now?: number): number {
  if (typeof val !== 'number' || !Number.isFinite(val)) {
    throw new Error('firstNoticedAt must be a valid timestamp number.')
  }
  const current = now ?? Date.now()
  const tenMinutesFuture = current + 10 * 60 * 1000
  if (val > tenMinutesFuture) {
    throw new Error('firstNoticedAt cannot be more than 10 minutes in the future.')
  }
  return val
}

export function validateResponseDueAt(
  val: unknown,
  firstNoticedAt: number,
): number {
  if (typeof val !== 'number' || !Number.isFinite(val)) {
    throw new Error('responseDueAt must be a valid timestamp number.')
  }
  if (val <= firstNoticedAt) {
    throw new Error('responseDueAt must be strictly after firstNoticedAt.')
  }
  return val
}

export function formatConIssueRegisterNo(
  kind: ConIssueKind,
  seq: number,
): string {
  return `${kind}-${String(seq).padStart(4, '0')}`
}
